import { useState, useRef, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Link2,
  Upload,
  Loader2,
  AlertCircle,
  ScanLine,
  Info,
  Zap,
  Globe,
  Music,
  FileVideo,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import UploadZone from "@/components/features/UploadZone";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

const API = `${process.env.REACT_APP_API_URL}/api`;
const IMAGE_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif"];
const VIDEO_EXTENSIONS = [".mp4", ".mov", ".avi", ".webm", ".mkv", ".m4v", ".mpg", ".mpeg"];
const AUDIO_EXTENSIONS = [".mp3", ".m4a", ".wav"];

function DecodingText({ text }) {
  const [displayed, setDisplayed] = useState(text);
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#@!";
  useEffect(() => {
    let iteration = 0;
    const interval = setInterval(() => {
      setDisplayed(
        text
          .split("")
          .map((char, idx) => {
            if (char === " ") return " ";
            if (idx < iteration) return char;
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join(""),
      );
      iteration += 0.5;
      if (iteration > text.length) clearInterval(interval);
    }, 30);
    return () => clearInterval(interval);
  }, [text]);
  return <span className="font-mono">{displayed}</span>;
}

export default function Analyze() {
  const navigate = useNavigate();
  const { checkAuth, user } = useAuth();
  const { t, i18n } = useTranslation();
  const STAGES = [
    t("analyze.stage0"),
    t("analyze.stage1"),
    t("analyze.stage2"),
    t("analyze.stage3"),
    t("analyze.stage4"),
    t("analyze.stage5"),
  ];
  const [tab, setTab] = useState("upload");
  const [file, setFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  const [error, setError] = useState(null);
  const [autoRechargeInfo, setAutoRechargeInfo] = useState(null);
  const intervalRef = useRef(null);
  const cancelPollingRef = useRef(false);
  const thumbnailPromiseRef = useRef(null);
  const isFreePlan = user?.plan === "free";

  const extractClientThumbnail = useCallback((videoFile) => {
    const promise = new Promise((resolve) => {
      const videoEl = document.createElement("video");
      videoEl.preload = "metadata";
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.crossOrigin = "anonymous";
      const objectUrl = URL.createObjectURL(videoFile);
      videoEl.src = objectUrl;

      const cleanup = () => {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch (_) {}
      };

      const drawFrame = () => {
        try {
          const vw = videoEl.videoWidth;
          const vh = videoEl.videoHeight;
          if (!vw || !vh) {
            cleanup();
            resolve(null);
            return;
          }
          const maxW = 480;
          // Preserve real orientation (portrait stays portrait)
          const scale = Math.min(1, maxW / Math.max(vw, vh));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(vw * scale);
          canvas.height = Math.round(vh * scale);
          canvas
            .getContext("2d")
            .drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
          setThumbnailPreview(dataUrl);
          resolve(dataUrl);
        } catch (_) {
          resolve(null);
        }
        cleanup();
      };

      videoEl.onloadedmetadata = () => {
        // Seek to 10% of duration, capped at 1s, minimum 0.1s
        const dur = videoEl.duration;
        const seekTo =
          isFinite(dur) && dur > 0
            ? Math.min(1, Math.max(0.1, dur * 0.1))
            : 0.1;
        videoEl.currentTime = seekTo;
      };

      videoEl.onseeked = drawFrame;

      // Fallback: if onseeked never fires (some encodings), draw at currentTime 0
      videoEl.onloadeddata = () => {
        if (videoEl.readyState >= 2 && videoEl.currentTime === 0) {
          setTimeout(() => {
            if (videoEl.currentTime === 0) drawFrame();
          }, 300);
        }
      };

      videoEl.onerror = () => {
        cleanup();
        resolve(null);
      };
      setTimeout(() => resolve(null), 6000);
    });
    thumbnailPromiseRef.current = promise;
    return promise;
  }, []);

  const handleFileSelect = useCallback(
    (selectedFile) => {
      if (selectedFile && isFreePlan) {
        const ext = `.${String(selectedFile.name || "").split(".").pop()?.toLowerCase()}`;
        if (VIDEO_EXTENSIONS.includes(ext) || AUDIO_EXTENSIONS.includes(ext)) {
          const upgradeMsg = t("analyze.fileTypeLockedForFree", {
            defaultValue: "Paket free hanya mendukung analisis gambar. Upgrade untuk video/audio.",
          });
          setFile(null);
          setThumbnailPreview(null);
          setError(upgradeMsg);
          toast.error(upgradeMsg);
          return;
        }
      }

      setFile(selectedFile);
      setThumbnailPreview(null);
      thumbnailPromiseRef.current = null;
      if (selectedFile) {
        const isImage = /\.(jpg|jpeg|png|webp|gif)$/i.test(selectedFile.name);
        if (isImage) {
          // For images, create a preview directly
          const reader = new FileReader();
          reader.onload = (e) => setThumbnailPreview(e.target.result);
          reader.readAsDataURL(selectedFile);
        } else if (
          /\.(mp4|mov|avi|webm|mkv|m4v|mpg|mpeg)$/i.test(selectedFile.name)
        ) {
          extractClientThumbnail(selectedFile);
        }
        // Audio files: no thumbnail
      }
    },
    [extractClientThumbnail, isFreePlan, t],
  );

  const startFakeProgress = () => {
    let prog = 0;
    let stage = 0;
    const thresholds = [15, 30, 50, 68, 82, 93];
    intervalRef.current = setInterval(() => {
      prog += Math.random() * 1.2 + 0.3;
      if (prog >= 93) prog = 93;
      const newStage = thresholds.findIndex((t) => prog < t);
      if (newStage !== -1 && newStage !== stage) {
        stage = newStage;
        setStageIdx(newStage);
      }
      setProgress(Math.floor(prog));
    }, 220);
  };

  const stopFakeProgress = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const handleAnalyze = async () => {
    if (tab === "upload" && !file) {
      toast.error(t("analyze.error.noFile"));
      return;
    }
    if (tab === "upload" && file && isFreePlan) {
      const ext = `.${String(file.name || "").split(".").pop()?.toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext) || AUDIO_EXTENSIONS.includes(ext)) {
        toast.error(
          t("analyze.fileTypeLockedForFree", {
            defaultValue: "Paket free hanya mendukung analisis gambar. Upgrade untuk video/audio.",
          }),
        );
        return;
      }
    }
    if (tab === "url" && !url.trim()) {
      toast.error(t("analyze.error.noUrl"));
      return;
    }

    setStatus("analyzing");
    setProgress(0);
    setStageIdx(0);
    setError(null);
    cancelPollingRef.current = false;
    startFakeProgress();

    try {
      // ── 1. Submit job (returns {job_id} immediately, no timeout risk) ──
      let submitRes;
      const lang =
        i18n.resolvedLanguage?.split("-")[0] ||
        i18n.language?.split("-")[0] ||
        "id";
      if (tab === "upload") {
        // Ensure thumbnail extraction has completed before submitting (handles race condition)
        let thumbToSend = thumbnailPreview;
        if (!thumbToSend && thumbnailPromiseRef.current) {
          thumbToSend = await Promise.race([
            thumbnailPromiseRef.current,
            new Promise((r) => setTimeout(() => r(null), 4000)),
          ]);
        }
        const formData = new FormData();
        formData.append("file", file);
        formData.append("language", lang);
        if (thumbToSend) formData.append("client_thumbnail", thumbToSend);
        submitRes = await axios.post(`${API}/analyze/upload`, formData, {
          timeout: 300000,
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        submitRes = await axios.post(
          `${API}/analyze/url`,
          { url: url.trim(), language: lang },
          { timeout: 300000, withCredentials: true },
        );
      }

      // ── 2. Poll job status every 3 s (each request < 1 s, no timeout risk) ──
      const { job_id } = submitRes.data;
      let result;
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        if (cancelPollingRef.current) return; // component unmounted / user cancelled

        const pollRes = await axios.get(`${API}/analyze/job/${job_id}`, {
          timeout: 15000,
          withCredentials: true,
        });
        const job = pollRes.data;

        if (job.status === "completed") {
          result = job.result;
          break;
        }
        if (job.status === "failed") {
          const e = new Error("Analysis failed");
          e._jobFailed = true;
          e._detail = job.error;
          e._code = job.error_code;
          throw e;
        }
        // still "processing" → keep polling
      }

      // ── 3. Done ──
      stopFakeProgress();
      setProgress(100);
      setStageIdx(STAGES.length - 1);
      await checkAuth();
      setTimeout(
        () => navigate(`/results/${result.id}`, { state: { result } }),
        600,
      );
    } catch (err) {
      stopFakeProgress();
      setStatus("error");

      // Unpack error detail — from job failure or direct HTTP error
      const detail = err._jobFailed ? err._detail : err.response?.data?.detail;
      const code = err._jobFailed ? err._code : err.response?.status;

      // Handle auto-recharge 402
      if (code === 402 && detail?.auto_recharge && detail?.payment_url) {
        setError(null);
        toast.info(t("analyze.autoRecharge.toast"), { duration: 5000 });
        setTimeout(() => window.open(detail.payment_url, "_blank"), 1500);
        setAutoRechargeInfo(detail);
        return;
      }

      const msg =
        typeof detail === "string"
          ? detail
          : detail?.message || err.message || t("common.error");
      setError(msg);
      toast.error(msg);
    }
  };

  useEffect(
    () => () => {
      stopFakeProgress();
      cancelPollingRef.current = true;
    },
    [],
  );

  const isAnalyzing = status === "analyzing";
  const canAnalyze = !isAnalyzing && (tab === "upload" ? !!file : !!url.trim());

  return (
    <main className="min-h-screen" data-testid="analyze-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
            {t("analyze.title")}
          </span>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-2">
            {t("analyze.title")}
          </h1>
          <p className="text-muted-foreground mt-3 text-base md:text-lg leading-relaxed">
            {t("analyze.subtitle")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="rounded-md border border-border bg-card p-6 md:p-8"
          data-testid="analyze-card"
        >
          {/* 2-Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-[7fr_3fr] gap-6 md:gap-8">

            {/* LEFT — Input Panel */}
            <div className="space-y-5">
              <Tabs
                value={tab}
                onValueChange={(v) => {
                  if (!isAnalyzing) {
                    setTab(v);
                    setError(null);
                  }
                }}
              >
                <TabsList className="rounded-md w-full" data-testid="analyze-tabs">
                  <TabsTrigger
                    value="upload"
                    className="flex-1 gap-2 rounded-sm"
                    data-testid="tab-upload"
                  >
                    <Upload className="w-3.5 h-3.5" /> {t("analyze.tab.upload")}
                  </TabsTrigger>
                  <TabsTrigger
                    value="url"
                    className="flex-1 gap-2 rounded-sm"
                    data-testid="tab-url"
                  >
                    <Link2 className="w-3.5 h-3.5" /> {t("analyze.tab.url")}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="upload" className="mt-4">
                  <UploadZone
                    onFileSelected={handleFileSelect}
                    disabled={isAnalyzing}
                    allowedExtensions={isFreePlan ? IMAGE_EXTENSIONS : undefined}
                    restrictionLabel={
                      isFreePlan
                        ? t("analyze.freeRestrictionBadge", { defaultValue: "Image Only (Free)" })
                        : null
                    }
                    helperText={
                      isFreePlan
                        ? t("analyze.freeRestrictionHint", {
                            defaultValue: "Image only - max 200MB. Upgrade untuk video/audio.",
                          })
                        : null
                    }
                  />
                </TabsContent>

                <TabsContent value="url" className="mt-4 space-y-2">
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                    {t("analyze.url.label")}
                  </label>
                  <Input
                    type="url"
                    placeholder={t("analyze.url.placeholder")}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={isAnalyzing}
                    className="font-mono text-sm rounded-sm"
                    data-testid="url-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("analyze.url.hint")}
                  </p>
                </TabsContent>
              </Tabs>

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex items-start gap-2 p-3 rounded-sm bg-red-500/10 border border-red-500/30"
                    data-testid="analyze-error"
                  >
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-400 font-mono">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Auto-recharge banner */}
              <AnimatePresence>
                {autoRechargeInfo && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-4 rounded-sm bg-brand-primary/10 border border-brand-primary/30"
                    data-testid="auto-recharge-banner"
                  >
                    <div className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-brand-primary mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">
                          {t("analyze.autoRecharge.title")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {t("analyze.autoRecharge.desc", {
                            credits: autoRechargeInfo.credits,
                            amount: autoRechargeInfo.amount?.toLocaleString("id-ID"),
                          })}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <a
                            href={autoRechargeInfo.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Button
                              size="sm"
                              className="rounded-sm bg-brand-primary text-white text-xs h-7 gap-1"
                              data-testid="auto-recharge-pay-btn"
                            >
                              <Zap className="w-3 h-3" />{" "}
                              {t("analyze.autoRecharge.pay")}
                            </Button>
                          </a>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="rounded-sm text-xs h-7"
                            onClick={() => setAutoRechargeInfo(null)}
                            data-testid="auto-recharge-dismiss-btn"
                          >
                            {t("analyze.autoRecharge.dismiss")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit Button */}
              <div className="space-y-3">
                <Button
                  onClick={handleAnalyze}
                  disabled={!canAnalyze}
                  size="lg"
                  className="w-full rounded-md bg-brand-primary hover:bg-brand-primary/90 text-white font-medium"
                  data-testid="analyze-submit-btn"
                >
                  {t("analyze.btn.analyze")}
                </Button>
                {/* P-A.1 — Free user public report warning */}
                {user?.plan === "free" && (
                  <div
                    className="flex items-start gap-3 p-3 rounded-sm border"
                    style={{
                      backgroundColor: "rgba(245,158,11,0.08)",
                      borderColor: "rgba(245,158,11,0.35)",
                    }}
                    data-testid="free-plan-public-notice"
                  >
                    <Globe
                      className="w-4 h-4 flex-shrink-0 mt-0.5"
                      style={{ color: "#f59e0b" }}
                    />
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: "#f59e0b" }}
                    >
                      {t("analyze.freePlanNotice")}{" "}
                      <Link to="/pricing" className="underline font-semibold">
                        {t("analyze.freePlanUpgrade")}
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT — Visual Panel */}
            <div className="flex flex-col">
              {isAnalyzing ? (
                /* Progress section */
                <div className="space-y-4" data-testid="analysis-progress">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ScanLine className="w-4 h-4 text-brand-primary animate-pulse" />
                        <span className="text-xs font-mono text-muted-foreground">
                          <DecodingText text={STAGES[stageIdx]} />
                        </span>
                      </div>
                      <span className="text-xs font-mono text-brand-primary font-medium">
                        {progress}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-brand-primary rounded-full"
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>
                  <div className="relative rounded-sm bg-secondary/50 overflow-hidden border border-border" style={{ minHeight: "180px" }}>
                    {thumbnailPreview ? (
                      <img
                        src={thumbnailPreview}
                        alt="Preview"
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                      />
                    ) : (
                      <img
                        src="https://images.unsplash.com/photo-1765445665883-085301570c87?w=600&q=60"
                        alt="Scanning"
                        className="absolute inset-0 w-full h-full object-cover opacity-20"
                      />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
                    </div>
                    <div
                      className="absolute left-0 right-0 h-0.5 bg-brand-primary/60 scan-beam"
                      style={{ top: 0 }}
                    />
                  </div>
                  <p className="text-xs text-center font-mono text-muted-foreground">
                    {t("analyze.waitNote")}
                  </p>
                </div>
              ) : file && tab === "upload" ? (
                /* File preview */
                <div className="rounded-sm border border-border overflow-hidden bg-secondary/30 flex flex-col">
                  {thumbnailPreview ? (
                    <img
                      src={thumbnailPreview}
                      alt="Preview"
                      className="w-full object-contain bg-black/20"
                      style={{ maxHeight: "260px" }}
                    />
                  ) : /\.(mp3|m4a|wav)$/i.test(file.name) ? (
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground flex-1">
                      <Music className="w-10 h-10 opacity-50" />
                      <span className="text-xs font-mono opacity-60">Audio file</span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground flex-1">
                      <FileVideo className="w-10 h-10 opacity-50" />
                      <Loader2 className="w-4 h-4 animate-spin opacity-40" />
                    </div>
                  )}
                  <div className="px-3 py-2 border-t border-border bg-secondary/20">
                    <p className="text-xs font-mono text-muted-foreground truncate">
                      {file.name}
                    </p>
                  </div>
                </div>
              ) : (
                /* Empty state placeholder */
                <div className="rounded-sm border border-dashed border-border flex flex-col items-center justify-center gap-3 text-center h-full min-h-[180px] p-6 bg-secondary/20">
                  <div className="w-12 h-12 rounded-sm bg-secondary border border-border flex items-center justify-center">
                    <Upload className="w-5 h-5 text-muted-foreground opacity-40" />
                  </div>
                  <p className="text-xs text-muted-foreground opacity-60 leading-relaxed">
                    Preview file<br />akan muncul di sini
                  </p>
                </div>
              )}
            </div>

          </div>
        </motion.div>

        {/* Tips */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {[
            { label: t("analyze.tip.bestLabel"), text: t("analyze.tip.best") },
            { label: t("analyze.tip.accuracyLabel"), text: t("analyze.tip.accuracy") },
            { label: t("analyze.tip.privacyLabel"), text: t("analyze.tip.privacy") },
          ].map(({ label, text }) => (
            <div
              key={label}
              className="p-4 rounded-sm border border-border bg-card/50"
            >
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
                {label}
              </div>
              <p className="text-xs text-muted-foreground">{text}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </main>
  );
}
